import { AlertTriangle, Flag, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { addDays, friendlyDay, monthDay, shortDay } from '../../shared/dates';
import { MAX_TREND_WINDOW_DAYS, MIN_TREND_WINDOW_DAYS } from '../../shared/trend';
import { formatWeight, type WeightUnit } from '../../shared/units';
import { useDays, useDeleteWeight, useSettings, useUpdateProfile, useWeights } from '../api/hooks';
import { InsightCard, RecapCard } from '../components/AICards';
import { BarChart, WeightChart } from '../components/charts';
import { amount, defFor, directionWord, unitLabel } from '../lib/format';
import { useUI } from '../lib/ui';
import { expandWeighInWindow, visibleWeighIns, weighInCountLabel, WEIGH_IN_PAGE_SIZE } from '../lib/weighInList';
import { weightHeadlineCopy } from '../lib/weightHeadline';

const RANGES = [
  { id: '30', label: '1M' },
  { id: '90', label: '3M' },
  { id: '180', label: '6M' },
  { id: 'all', label: 'All' },
] as const;

const CHANGE_PERIODS = [
  { id: 'd7', label: '7 days' },
  { id: 'd30', label: '30 days' },
  { id: 'd90', label: '90 days' },
  { id: 'all', label: 'All time' },
] as const;

function formatDelta(kg: number | null | undefined, unit: WeightUnit): string {
  if (kg == null) return '—';
  return formatWeight(kg, unit, { signed: true });
}

function TrendWindowControl({ days }: { days: number }) {
  const { toast } = useUI();
  const update = useUpdateProfile();
  const [text, setText] = useState(String(days));
  useEffect(() => setText(String(days)), [days]);

  const commit = () => {
    const n = Number(text);
    if (!Number.isInteger(n) || n < MIN_TREND_WINDOW_DAYS || n > MAX_TREND_WINDOW_DAYS) {
      setText(String(days));
      toast(`Use a trend window from ${MIN_TREND_WINDOW_DAYS} to ${MAX_TREND_WINDOW_DAYS} days`, { tone: 'error' });
      return;
    }
    if (n !== days) update.mutate({ trendWindowDays: n }, { onError: (e) => toast(e.message, { tone: 'error' }) });
  };

  return (
    <label className="row" style={{ gap: 6 }}>
      <span className="tiny muted">Window</span>
      <input
        className="input input--sm num"
        inputMode="numeric"
        aria-label="Trend window in days"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        style={{ width: 64 }}
      />
      <span className="tiny muted">days</span>
    </label>
  );
}

function WeightTab() {
  const { today, openWeighIn, toast } = useUI();
  const navigate = useNavigate();
  const [range, setRange] = useState<string>('90');
  // How many of the newest weigh-ins in this range are listed. "Show more" adds a page; a new range starts over.
  const [visible, setVisible] = useState(WEIGH_IN_PAGE_SIZE);
  const selectRange = (id: string) => {
    if (id === range) return;
    setRange(id);
    setVisible(WEIGH_IN_PAGE_SIZE);
  };
  const weights = useWeights(range, today);
  const settings = useSettings();
  const remove = useDeleteWeight();
  const unit = weights.data?.unit ?? settings.data?.profile.weightUnit ?? 'lb';
  const windowDays = weights.data?.trendWindowDays ?? settings.data?.profile.trendWindowDays ?? 14;
  const aiEnabled = settings.data?.ai.enabled ?? false;
  const s = weights.data?.summary;
  const headline = weightHeadlineCopy(s, windowDays, today);
  const rateLine = s?.rate ? `${formatWeight(s.rate.kgPerWeek, unit, { signed: true, decimals: 2 })} / week` : null;
  const trendByDate = useMemo(() => new Map(weights.data?.trend.map((t) => [t.date, t.kg])), [weights.data]);
  // The current stretch: everything counts from its starting weigh-in; earlier ones show faded.
  const since = s?.since ?? null;
  const start = since && s?.startKg != null ? { date: since, kg: s.startKg } : null;
  const entries = weights.data?.entries ?? [];
  const startInRange = since !== null && entries.some((w) => w.date <= since);
  const earlierInRange = since !== null && entries.some((w) => w.date < since);
  const weighIns = visibleWeighIns(entries, visible);

  return (
    <div className="stack" style={{ gap: 16 }}>
      {start && (
        <div className="card stretch">
          <Flag size={16} aria-hidden="true" />
          <span className="grow small num">
            <b>Since {shortDay(start.date, today)}</b> · started at {formatWeight(start.kg, unit)}
          </span>
          <button className="btn btn--quiet btn--xs" onClick={() => openWeighIn({ date: start.date })}>
            Edit<span className="sr-only"> starting weight</span>
          </button>
        </div>
      )}
      <div className="stats">
        <div className="card stat">
          <span className="stat__label">{headline.shown ? headline.title : 'Recent low'}</span>
          <span className="stat__value num">{headline.shown ? formatWeight(headline.shown.kg, unit) : '—'}</span>
          {headline.shown && headline.shown.source !== 'trend' ? (
            <>
              {headline.detail && <span className="stat__sub num">{headline.detail}</span>}
              {s?.trendKg != null && (
                <span className="weight-trend num">
                  Trend {formatWeight(s.trendKg, unit)}
                  {rateLine ? ` · ${rateLine}` : ''}
                </span>
              )}
              {!rateLine && <span className="stat__sub">Need ~10 days of weigh-ins</span>}
            </>
          ) : (
            <span className="stat__sub num">{rateLine ?? 'Need ~10 days of weigh-ins'}</span>
          )}
        </div>
        <div className="card stat">
          <span className="stat__label">To goal</span>
          <span className="stat__value num">{s?.toGoKg != null ? formatWeight(Math.abs(s.toGoKg), unit) : '—'}</span>
          {s?.percentComplete != null ? (
            <span className="stat__sub">{s.percentComplete}% of the way</span>
          ) : s && s.goalKg === null ? (
            // No goal weight (or counting without goals): setup's goal step sets one.
            <button className="btn btn--quiet btn--xs stat__link" onClick={() => navigate('/setup', { state: { start: 'goal', returnTo: '/progress' } })}>
              Set a goal
            </button>
          ) : (
            <span className="stat__sub">Set a goal in You</span>
          )}
        </div>
        <div className="card stat">
          <span className="stat__label">On pace for</span>
          <span className="stat__value">{s?.projection ? monthDay(s.projection.date) : '—'}</span>
          <span className="stat__sub">
            {s?.projection
              ? s.projection.latest
                ? `${monthDay(s.projection.earliest)} – ${monthDay(s.projection.latest)}`
                : `${monthDay(s.projection.earliest)} or later`
              : s && s.goalKg === null
                ? 'No goal weight yet'
                : 'Not trending toward goal yet'}
          </span>
        </div>
      </div>

      {s?.trendKg != null && (
        <section className="card card--pad">
          <div className="card__head">
            <div>
              <h2 className="card__title">Change</h2>
              <p className="tiny muted" style={{ margin: '2px 0 0' }}>
                Trend now versus the trend then
              </p>
            </div>
            <TrendWindowControl days={windowDays} />
          </div>
          <div className="metrics" aria-label="Weight change from the trend">
            {CHANGE_PERIODS.map((p) => (
              <div key={p.id}>
                <div className="stat__label">{p.id === 'all' && since ? 'Since start' : p.label}</div>
                <div className="metric__value num">{formatDelta(s.changes[p.id], unit)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {s?.forecasts && s.forecasts.length > 0 && (
        <section className="card card--pad">
          <div className="card__head">
            <div>
              <h2 className="card__title">If this rate holds</h2>
              <p className="tiny muted" style={{ margin: '2px 0 0' }}>
                {s.rate ? `${formatWeight(s.rate.kgPerWeek, unit, { signed: true, decimals: 2 })} per week, with a range` : 'From the current trend'}
              </p>
            </div>
          </div>
          <div className="metrics metrics--3" aria-label="Projected weight">
            {s.forecasts.map((f) => (
              <div key={f.days}>
                <div className="stat__label">
                  {f.days} days · {monthDay(f.date)}
                </div>
                <div className="metric__value num">{formatWeight(f.kg, unit)}</div>
                <div className="tiny muted num">
                  {formatWeight(f.lowKg, unit)} – {formatWeight(f.highKg, unit)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {s?.flags.rapidLoss && (
        <div className="banner banner--warn" role="alert">
          <AlertTriangle size={18} />
          <span className="small">
            <b>You're losing faster than 1% of your weight per week.</b> That pace can cost muscle and energy. Consider eating a bit more, and check in
            with a clinician if it continues.
            {s.flags.rapidLossBeforeStart &&
              ' This counts weigh-ins from before your starting weight too, so a change like a new scale can show up here for a few weeks.'}
          </span>
        </div>
      )}

      <section className="card card--pad">
        <div className="card__head">
          <h2 className="card__title">Weight</h2>
          <div className="seg" role="radiogroup" aria-label="Range">
            {RANGES.map((r) => (
              <button key={r.id} role="radio" aria-checked={range === r.id} className={`seg__btn ${range === r.id ? 'is-active' : ''}`} onClick={() => selectRange(r.id)}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {weights.data ? (
          <WeightChart
            points={weights.data.entries}
            trend={weights.data.trend}
            goalKg={s?.goalKg ?? null}
            unit={unit}
            today={today}
            start={start}
            forecastRate={s?.forecasts && s.rate ? s.rate.kgPerWeek : null}
            forecastSe={s?.forecasts && s.rate ? s.rate.seKgPerWeek : null}
          />
        ) : (
          <div className="skeleton" style={{ height: 240 }} />
        )}
        <div className="legend" style={{ marginTop: 10 }}>
          <span>
            <span className="legend__swatch" style={{ background: 'var(--ink)' }} />
            {windowDays}-day trend
          </span>
          <span>
            <span className="legend__swatch" style={{ background: 'var(--faint)', height: 6, width: 6, borderRadius: 3 }} />
            Weigh-ins
          </span>
          <span>
            <span className="legend__swatch" style={{ background: 'var(--fiber)' }} />
            Goal
          </span>
          {startInRange && (
            <span>
              <span className="legend__swatch legend__swatch--start" />
              Start
            </span>
          )}
          {earlierInRange && (
            <span>
              <span className="legend__swatch legend__swatch--before-start" />
              Before start
            </span>
          )}
          {s?.forecasts && (
            <span>
              <span className="legend__swatch" style={{ background: 'var(--ai)' }} />
              Forecast
            </span>
          )}
        </div>
      </section>

      <InsightCard aiEnabled={aiEnabled} />
      <RecapCard aiEnabled={aiEnabled} unit={unit} />

      <section className="card card--pad">
        <div className="card__head">
          <h2 className="card__title">Weigh-ins</h2>
          <button className="btn btn--accent btn--sm" onClick={() => openWeighIn()}>
            <Plus size={16} /> Weigh in
          </button>
        </div>
        <div className="list">
          {weighIns.shown.map((w) => {
            const trend = trendByDate.get(w.date);
            const delta = trend !== undefined ? w.kg - trend : null;
            return (
              <div className="list-item" key={w.id}>
                <div className="grow">
                  <div style={{ fontWeight: 600 }}>{friendlyDay(w.date, today)}</div>
                  {w.date === since ? (
                    <span className="badge" style={{ marginTop: 3 }}>
                      <Flag size={11} aria-hidden="true" /> Starting weight
                    </span>
                  ) : since !== null && w.date < since ? (
                    // An earlier mark is where the numbers go back to if the current start goes.
                    <div className="tiny muted">{w.startingWeight ? 'Earlier start' : 'Before your start'}</div>
                  ) : (
                    delta !== null && Math.abs(delta) >= 0.05 && <div className="tiny muted num">{formatWeight(delta, unit, { signed: true })} vs trend</div>
                  )}
                </div>
                <span className="num" style={{ fontWeight: 650, whiteSpace: 'nowrap' }}>
                  {formatWeight(w.kg, unit)}
                </span>
                <button className="btn btn--quiet btn--icon btn--sm" aria-label={`Edit weigh-in from ${friendlyDay(w.date, today)}`} onClick={() => openWeighIn({ date: w.date })}>
                  <Pencil size={15} />
                </button>
                <button
                  className="btn btn--quiet btn--icon btn--sm"
                  aria-label={`Delete weigh-in from ${friendlyDay(w.date, today)}`}
                  onClick={() =>
                    remove.mutate(w.id, {
                      onSuccess: () =>
                        toast(
                          w.date === since
                            ? 'Starting weigh-in deleted. Your numbers now count from an earlier starting weight, or from all your weigh-ins.'
                            : 'Weigh-in deleted',
                        ),
                      onError: (e) => toast(e.message, { tone: 'error' }),
                    })
                  }
                >
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
          {weights.data && weights.data.entries.length === 0 && <div className="empty small">No weigh-ins in this range.</div>}
        </div>
        {weighIns.total > WEIGH_IN_PAGE_SIZE && (
          <div className="stack" style={{ gap: 8, alignItems: 'center', marginTop: 10 }}>
            <div className="tiny muted" aria-live="polite">
              {weighInCountLabel(weighIns.shownCount, weighIns.total)}
            </div>
            {weighIns.hasMore && (
              <button className="btn btn--quiet btn--sm" onClick={() => setVisible((count) => expandWeighInWindow(count))}>
                Show more
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function NutritionTab() {
  const { today } = useUI();
  const [span, setSpan] = useState<7 | 30>(7);
  const settings = useSettings();
  const from = addDays(today, -(span - 1));
  const days = useDays(from, today);
  const tracked = (settings.data?.tracking ?? []).filter((t) => t.tracked);
  const [key, setKey] = useState('kcal');
  const defs = useMemo(() => new Map((settings.data?.nutrients ?? []).map((n) => [n.key, n])), [settings.data]);
  const def = defFor(key, defs);
  const goal = tracked.find((t) => t.key === key);
  const goalAmount = goal?.target ?? null;
  const goalDirection = goal?.direction ?? 'target';
  // A day counts as logged when it has any entry, including a daily food's copy.
  const series = (days.data ?? []).map((d) => ({ date: d.date, amount: d.loggedCount ? (d.totals[key] ?? 0) : null }));
  const logged = series.filter((d) => d.amount !== null);
  const avg = logged.length ? logged.reduce((s, d) => s + (d.amount ?? 0), 0) / logged.length : undefined;
  const hits =
    goalAmount !== null && goalAmount > 0
      ? logged.filter((d) => {
          const v = d.amount ?? 0;
          if (goalDirection === 'min') return v >= goalAmount;
          if (goalDirection === 'max') return v <= goalAmount;
          return Math.abs(v - goalAmount) <= goalAmount * 0.1;
        }).length
      : null;
  const partialDays = (days.data ?? []).filter((d) => d.loggedCount > 0 && (d.known[key] ?? 0) < d.entryCount).length;

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row between wrap">
        <div className="scroll-x" style={{ margin: 0, padding: 0, flex: '1 1 auto' }} role="tablist" aria-label="Nutrient">
          {tracked.map((t) => (
            <button key={t.key} role="tab" aria-selected={key === t.key} className={`chip ${key === t.key ? 'is-active' : ''}`} onClick={() => setKey(t.key)}>
              {defFor(t.key, defs).name}
            </button>
          ))}
        </div>
        <div className="seg" role="radiogroup" aria-label="Range">
          {([7, 30] as const).map((n) => (
            <button key={n} role="radio" aria-checked={span === n} className={`seg__btn ${span === n ? 'is-active' : ''}`} onClick={() => setSpan(n)}>
              {n}D
            </button>
          ))}
        </div>
      </div>
      <div className="stats">
        <div className="card stat">
          <span className="stat__label">Daily average</span>
          <span className="stat__value num">
            {amount(avg, def)} <span style={{ fontSize: 15 }}>{unitLabel(def)}</span>
          </span>
          <span className="stat__sub">
            {goalAmount !== null ? `${directionWord(goalDirection)} ${amount(goalAmount, def)} ${unitLabel(def)}` : 'No target'}
          </span>
        </div>
        <div className="card stat">
          <span className="stat__label">Days logged</span>
          <span className="stat__value num">
            {logged.length}/{span}
          </span>
          <span className="stat__sub">Averages use logged days only</span>
        </div>
        <div className="card stat">
          <span className="stat__label">On target</span>
          <span className="stat__value num">{hits === null ? '—' : `${hits}/${logged.length}`}</span>
          <span className="stat__sub">logged days</span>
        </div>
      </div>
      <section className="card card--pad">
        <div className="card__head">
          <h2 className="card__title">{def.name}</h2>
          <span className="tiny muted">dashed line = target</span>
        </div>
        {days.data ? (
          <BarChart days={series} target={goalAmount} direction={goalDirection} unit={unitLabel(def)} label={def.name} />
        ) : (
          <div className="skeleton" style={{ height: 200 }} />
        )}
        {partialDays > 0 && key !== 'kcal' && (
          <p className="tiny muted" style={{ marginTop: 8 }}>
            {partialDays} {partialDays === 1 ? 'day has' : 'days have'} foods without {def.name.toLowerCase()} data, so those totals are minimums.
          </p>
        )}
      </section>
    </div>
  );
}

export function ProgressPage() {
  const [tab, setTab] = useState<'weight' | 'nutrition'>('weight');
  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Progress</h1>
          <p className="page__sub">Trends beat single days.</p>
        </div>
      </div>
      <div className="seg seg--block" role="tablist" aria-label="Progress view" style={{ maxWidth: 360 }}>
        {(['weight', 'nutrition'] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={`seg__btn ${tab === t ? 'is-active' : ''}`} onClick={() => setTab(t)}>
            {t === 'weight' ? 'Weight' : 'Nutrition'}
          </button>
        ))}
      </div>
      {tab === 'weight' ? <WeightTab /> : <NutritionTab />}
    </div>
  );
}
